import { useState, useEffect } from 'react';
import { Group, Text, Badge, Loader, Menu, Tooltip, ActionIcon } from '@mantine/core';
import { 
  IconRefresh, 
  IconChevronDown, 
  IconCheck
} from '@tabler/icons-react';
import { apiService } from '../../lib/api';
import { PinEntryModal, type Environment as PinEnvironment } from '../PinEntryModal';
import { ConfirmationModal } from '../Utils';
import './styles.css';

export type Environment = 'dev' | 'qa' | 'production' | 'production-india';

interface EnvironmentInfo {
  currentEnvironment: Environment;
  availableEnvironments: Environment[];
}

export function EnvironmentSwitcher() {
  const [currentEnv, setCurrentEnv] = useState<Environment>('dev');
  const [availableEnvs, setAvailableEnvs] = useState<Environment[]>(['dev', 'qa', 'production', 'production-india']);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinModalOpened, setPinModalOpened] = useState(false);
  const [targetEnvironment, setTargetEnvironment] = useState<Environment | null>(null);
  const [cacheClearModalOpened, setCacheClearModalOpened] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheClearError, setCacheClearError] = useState<string | null>(null);

  // Fetch current environment on mount
  useEffect(() => {
    fetchCurrentEnvironment();
  }, []);

  const fetchCurrentEnvironment = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<{
        success: boolean;
        data: EnvironmentInfo;
        timestamp: string;
      }>('/api/environment/current');
      
      if (response.success && response.data) {
        const newEnv = response.data.currentEnvironment;
        setCurrentEnv(newEnv);
        setAvailableEnvs(response.data.availableEnvironments);
        
        // Store environment in localStorage for OrganizationContext
        try {
          localStorage.setItem('currentEnvironment', newEnv);
        } catch (error) {
          console.warn('Failed to store environment in localStorage:', error);
        }
        
        // Dispatch event for OrganizationContext to listen
        window.dispatchEvent(new CustomEvent('environmentChanged', { detail: newEnv }));
      }
    } catch (err: any) {
      console.error('Failed to fetch current environment:', err);
      
      // Extract error message from various error formats
      let errorMessage = 'Failed to fetch environment';
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error?.message) {
        errorMessage = err.error.message;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error?.message) {
        errorMessage = err.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEnvironmentSelect = (newEnv: Environment | null) => {
    if (!newEnv || newEnv === currentEnv) {
      return;
    }

    // Open PIN modal
    setTargetEnvironment(newEnv);
    setPinModalOpened(true);
    setError(null);
  };

  const handlePinConfirm = async (pin: string) => {
    if (!targetEnvironment) {
      return;
    }

    try {
      setSwitching(true);
      setError(null);

      const response = await apiService.post<{
        success: boolean;
        data: {
          success: boolean;
          previousEnvironment: Environment;
          currentEnvironment: Environment;
          message: string;
        };
        timestamp: string;
      }>('/api/environment/switch', {
        environment: targetEnvironment,
        pin: pin,
      });

      if (response.success && response.data) {
        const newEnv = response.data.currentEnvironment;
        setCurrentEnv(newEnv);
        console.log(response.data.message);
        
        // Store environment in localStorage for OrganizationContext
        try {
          localStorage.setItem('currentEnvironment', newEnv);
        } catch (error) {
          console.warn('Failed to store environment in localStorage:', error);
        }
        
        // Dispatch event for OrganizationContext to listen
        window.dispatchEvent(new CustomEvent('environmentChanged', { detail: newEnv }));
        
        // Close modal
        setPinModalOpened(false);
        
        // Add fade-out animation to the entire page
        const htmlElement = document.documentElement;
        htmlElement.classList.add('page-fade-out');
        
        // Wait for animation, then redirect to organizations page
        setTimeout(() => {
          window.location.href = '/organizations';
        }, 600); // Match animation duration
      }
    } catch (err: any) {
      console.error('Failed to switch environment:', err);
      
      // Extract error message from various error formats
      let errorMessage = 'Failed to switch environment';
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error?.message) {
        errorMessage = err.error.message;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error?.message) {
        errorMessage = err.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      }
      
      setError(errorMessage);
      setSwitching(false);
      // Don't close modal on error, let user try again
      // Throw a new Error with the extracted message so PinEntryModal can display it properly
      throw new Error(errorMessage);
    }
  };

  const handlePinModalClose = () => {
    if (!switching) {
      setPinModalOpened(false);
      setTargetEnvironment(null);
      setError(null);
    }
  };

  const handleClearCacheClick = () => {
    setCacheClearModalOpened(true);
    setCacheClearError(null);
  };

  const handleClearCacheConfirm = async () => {
    try {
      setClearingCache(true);
      setCacheClearError(null);
      await apiService.post('/api/cache/clear', {});
      console.log('Cache cleared successfully');
      setCacheClearModalOpened(false);
      // Show success message briefly
      setTimeout(() => {
        setClearingCache(false);
      }, 500);
    } catch (err: any) {
      console.error('Failed to clear cache:', err);
      
      // Extract error message from various error formats
      let errorMessage = 'Failed to clear cache';
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error?.message) {
        errorMessage = err.error.message;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error?.message) {
        errorMessage = err.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      }
      
      setCacheClearError(errorMessage);
      setClearingCache(false);
    }
  };

  const handleCacheClearModalClose = () => {
    if (!clearingCache) {
      setCacheClearModalOpened(false);
      setCacheClearError(null);
    }
  };

  const getEnvironmentColor = (env: Environment): string => {
    switch (env) {
      case 'dev':
        return 'blue';
      case 'qa':
        return 'yellow';
      case 'production':
        return 'red';
      case 'production-india':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getEnvironmentLabel = (env: Environment): string => {
    switch (env) {
      case 'dev':
        return 'Development';
      case 'qa':
        return 'QA';
      case 'production':
        return 'Production';
      case 'production-india':
        return 'Production India';
      default:
        return env;
    }
  };

  if (loading && !currentEnv) {
    return (
      <div className="env-switcher-container">
        <Loader size="sm" />
      </div>
    );
  }

  return (
    <>
      <div className="env-switcher-container">
        <Group gap="md" align="center" wrap="nowrap">
          {/* Environment Selector */}
          <Menu
            shadow="md"
            width={200}
            position="bottom-end"
            disabled={switching}
            withArrow
          >
            <Menu.Target>
              <button className="env-selector-button" disabled={switching}>
                <Group gap={6} wrap="nowrap">
                  <Badge
                    color={getEnvironmentColor(currentEnv)}
                    size="md"
                    variant="filled"
                    className="env-badge"
                  >
                    {getEnvironmentLabel(currentEnv)}
                  </Badge>
                  {switching ? (
                    <Loader size="xs" />
                  ) : (
                    <IconChevronDown size={14} className="env-chevron" />
                  )}
                </Group>
              </button>
            </Menu.Target>

            <Menu.Dropdown className="env-menu-dropdown">
              <Menu.Label>Switch Environment</Menu.Label>
              {availableEnvs.map((env) => (
                <Menu.Item
                  key={env}
                  onClick={() => handleEnvironmentSelect(env)}
                  disabled={env === currentEnv || switching}
                  className={`env-menu-item ${env === currentEnv ? 'active' : ''}`}
                  leftSection={
                    <Badge
                      color={getEnvironmentColor(env)}
                      size="sm"
                      variant="dot"
                    />
                  }
                  rightSection={
                    env === currentEnv ? (
                      <IconCheck size={16} className="env-check-icon" />
                    ) : null
                  }
                >
                  <div className="env-menu-item-content">
                    <Text size="sm" fw={env === currentEnv ? 600 : 400}>
                      {getEnvironmentLabel(env)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {env === currentEnv ? 'Current' : 'Click to switch'}
                    </Text>
                  </div>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          {/* Clear Cache Button */}
          <Tooltip label="Clear all cache (in-memory & Redis)" position="bottom" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              radius="md"
              onClick={handleClearCacheClick}
              disabled={clearingCache || loading || switching}
              loading={clearingCache}
              className="cache-clear-button"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {error && (
          <Text size="xs" c="red" className="env-error">
            {error}
          </Text>
        )}
      </div>

      {targetEnvironment && (
        <PinEntryModal
          opened={pinModalOpened}
          onClose={handlePinModalClose}
          environment={targetEnvironment as PinEnvironment}
          onConfirm={handlePinConfirm}
          isSwitching={switching}
        />
      )}

      {/* Cache Clear Confirmation Modal */}
      <ConfirmationModal
        opened={cacheClearModalOpened}
        onClose={handleCacheClearModalClose}
        onConfirm={handleClearCacheConfirm}
        title="Clear Cache"
        message="This will clear all cached data from both in-memory cache and Redis. This action cannot be undone and may temporarily impact application performance as data will need to be reloaded."
        confirmLabel="Clear Cache"
        cancelLabel="Cancel"
        confirmColor="orange"
        loading={clearingCache}
        error={cacheClearError}
        warning={true}
      />
    </>
  );
}

